// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "hooks/useAuth";
import { Box, Typography, Alert, AlertTitle, Card } from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";

import { LoadingButton } from "@mui/lab";
import Pricing from "components/dashboard/upgrade/Pricing";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { getTrialLengthForRole } from "constants/trials";

const UpgradePage = () => {
  const {
    userData,
    subscription,
    subscriptionData,
    hasActiveSubscription,
    hasLapsedSubscription,
    canStartTrial,
    hasActiveTrial,
    trialExpired,
  } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const [manageBillingLoading, setManageBillingLoading] = useState(false);

  const handleManageBilling = async () => {
    setManageBillingLoading(true);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        // No flowType = regular portal with billing history, payment methods, invoices, reactivation
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert(t("upgrade.portalFailed"));
      setManageBillingLoading(false);
    }
  };

  const getUpgradeMessage = (
    role,
    canStartTrial,
    trialExpired,
    hasLapsedSubscription
  ) => {
    // Active subscription - show current plan message
    if (hasActiveSubscription) {
      if (subscriptionData?.cancel_at_period_end) {
        return t("upgrade.message.cancelScheduled");
      }
      if (role === "facility") {
        return t("upgrade.message.activeFacility");
      }
      return t("upgrade.message.activeMember");
    }

    // Lapsed subscription - different messaging based on status
    if (hasLapsedSubscription) {
      if (subscriptionData?.status === "past_due") {
        return t("upgrade.message.pastDue");
      }
      if (subscriptionData?.status === "canceled") {
        return t("upgrade.message.canceled");
      }
      return t("upgrade.message.inactive");
    }

    const isTrialRole =
      role === "patient" || role === "professional" || role === "facility";

    if (isTrialRole && canStartTrial) {
      const trialLengthDays = getTrialLengthForRole(role);
      if (role === "facility") {
        return t("upgrade.message.trialFacility", { count: trialLengthDays });
      }
      return t("upgrade.message.trialIndividual", { count: trialLengthDays });
    }

    if (isTrialRole && trialExpired) {
      if (role === "facility") {
        return t("upgrade.message.trialEndedFacility");
      }
      return t("upgrade.message.trialEnded");
    }

    // Standard messaging for other cases
    if (role === "facility") {
      return t("upgrade.message.upgradeFacility");
    }
    if (role === "patient") {
      return t("upgrade.message.upgradePatient");
    }
    if (role === "professional") {
      return t("upgrade.message.upgradeProfessional");
    }
    return t("upgrade.message.upgradeDefault");
  };

  const getPageTitle = (
    role,
    canStartTrial,
    trialExpired,
    hasLapsedSubscription
  ) => {
    if (hasLapsedSubscription) {
      if (subscriptionData?.status === "past_due") {
        return t("upgrade.title.reactivate");
      }
      if (subscriptionData?.status === "canceled") {
        return t("upgrade.title.choosePlan");
      }
      return t("upgrade.title.resubscribe");
    }

    if (role === "patient" || role === "professional" || role === "facility") {
      if (canStartTrial) {
        return t("upgrade.title.startTrial");
      }
      return t("upgrade.title.choosePlan");
    }

    return t("upgrade.title.upgrade");
  };

  const shouldShowPricing = () => {
    // For past_due subscriptions, don't show pricing - portal can handle reactivation
    if (hasLapsedSubscription && subscriptionData?.status === "past_due") {
      return false;
    }

    // For canceled/ended subscriptions, show pricing - portal can't reactivate these
    if (hasLapsedSubscription && subscriptionData?.status === "canceled") {
      return true;
    }

    return true;
  };

  return (
    <Box sx={{ pb: 4 }}>
      <DashboardPageHeader
        title={getPageTitle(
          userData?.role,
          canStartTrial,
          trialExpired,
          hasLapsedSubscription
        )}
        subtitle={false}
        sx={{ mb: 2 }}
      />

      {hasActiveTrial && (
        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
          <AlertTitle>{t("upgrade.early.title")}</AlertTitle>
          {t("upgrade.early.body")}
        </Alert>
      )}

      <Typography
        variant="body"
        sx={{
          display: "block",
          mt: 1,
          mb: userData?.role === "facility" && hasActiveSubscription ? 5 : 2,
        }}
      >
        {getUpgradeMessage(
          userData?.role,
          canStartTrial,
          trialExpired,
          hasLapsedSubscription
        )}
      </Typography>

      {/* Past Due Subscription - Show Manage Billing Button */}
      {hasLapsedSubscription && subscriptionData?.status === "past_due" && (
        <Card
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            my: 4,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            {t("upgrade.pastDue.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t("upgrade.pastDue.body")}
          </Typography>
          <LoadingButton
            loading={manageBillingLoading}
            disabled={manageBillingLoading}
            variant="contained"
            size="large"
            onClick={handleManageBilling}
            sx={{ px: 4, py: 1.5 }}
          >
            {t("upgrade.pastDue.manageBilling")}
          </LoadingButton>
        </Card>
      )}

      {shouldShowPricing() && (
        <Pricing
          uid={userData?.uid}
          role={userData?.role}
          subscription={subscription}
        />
      )}
    </Box>
  );
};

export default UpgradePage;
